import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDateShort, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Calendar, MapPin, ArrowRight, Instagram, Globe, Sparkles, CheckCircle2, Ticket } from "lucide-react";

export default function OrganizerEvents() {
  const { organizerSlug, slug } = useParams();
  const currentSlug = organizerSlug || slug;
  const [organizer, setOrganizer] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!currentSlug) return;
    setLoading(true);
    setError(false);
    Promise.all([
      api.get(`/organizers/${currentSlug}`).then((r) => r.data).catch(() => null),
      api.get(`/organizers/${currentSlug}/events`).then((r) => r.data).catch(() => []),
    ])
      .then(([orgData, eventsData]) => {
        if (!orgData) {
          setError(true);
        } else {
          setOrganizer(orgData);
          setEvents(eventsData);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [currentSlug]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-7xl mx-auto px-4 py-24 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-stone-800" />
          <p className="mt-4 text-stone-500 text-sm">Loading organizer profile…</p>
        </div>
      </PublicLayout>
    );
  }

  if (error || !organizer) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <h2 className="text-2xl font-bold font-display text-stone-900 dark:text-stone-100">Organizer Not Found</h2>
          <p className="mt-2 text-stone-500 text-sm">The organizer profile you are looking for does not exist or has been relocated.</p>
          <Link to="/events" className="mt-6 inline-flex items-center gap-2 btn-brand px-4 py-2 rounded-md text-sm font-medium">
            Browse All Events <ArrowRight size={14} />
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {/* Organizer Hero Header */}
      <section className="relative overflow-hidden bg-gradient-to-b from-stone-900 to-stone-950 text-white py-16 sm:py-20 border-b border-stone-800">
        <div className="absolute inset-0 cgc-noise pointer-events-none opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div className="flex items-center gap-5">
              {organizer.logo_url ? (
                <img
                  src={organizer.logo_url}
                  alt={organizer.name}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-2 border-stone-700 shadow-xl bg-stone-800"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center font-display font-black text-3xl text-white shadow-xl">
                  {organizer.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-400 border border-orange-500/30 mb-2">
                  <CheckCircle2 size={12} /> Verified Event Organizer
                </div>
                <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                  {organizer.name}
                </h1>
                {organizer.tagline && (
                  <p className="mt-1 text-base text-stone-300 font-medium">
                    {organizer.tagline}
                  </p>
                )}
              </div>
            </div>

            {/* Social & Contact Actions */}
            <div className="flex items-center gap-3">
              {organizer.instagram_handle && (
                <a
                  href={`https://instagram.com/${organizer.instagram_handle.replace(/^@/, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs font-medium border border-stone-700 transition-colors"
                >
                  <Instagram size={14} className="text-pink-400" /> @{organizer.instagram_handle.replace(/^@/, "")}
                </a>
              )}
              {organizer.website_url && (
                <a
                  href={organizer.website_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-stone-800 hover:bg-stone-700 text-stone-200 text-xs font-medium border border-stone-700 transition-colors"
                >
                  <Globe size={14} className="text-blue-400" /> Website ↗
                </a>
              )}
            </div>
          </div>

          {organizer.description && (
            <p className="mt-6 text-sm sm:text-base text-stone-400 max-w-3xl leading-relaxed">
              {organizer.description}
            </p>
          )}

          <div className="mt-8 pt-4 border-t border-stone-800/80 flex items-center justify-between text-xs text-stone-400">
            <span className="flex items-center gap-1.5">
              <Sparkles size={13} className="text-orange-400" /> Official Ticketing Partner · Common Ground Collective
            </span>
            <span>{events.length} active events</span>
          </div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="eyebrow">Upcoming Schedule</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1">Events by {organizer.name}</h2>
          </div>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-16 bg-stone-50 dark:bg-stone-900/40 rounded-2xl border border-dashed border-stone-300 dark:border-stone-800">
            <Ticket className="w-10 h-10 text-stone-400 mx-auto mb-3" />
            <h3 className="font-display font-semibold text-lg">No active events announced yet</h3>
            <p className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
              Follow {organizer.name} on their social channels for early drop announcements.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev) => {
              const lowestPrice = Math.min(...(ev.tiers || []).map((t) => t.price_paise), 0);
              return (
                <Link
                  key={ev.id}
                  to={`/events/${organizerSlug}/${ev.slug}`}
                  className="group cgc-card overflow-hidden flex flex-col hover:border-[color:var(--brand-terracotta)] transition-colors"
                >
                  <div className="relative h-48 w-full overflow-hidden bg-stone-100">
                    <img
                      src={ev.cover_image || "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7"}
                      alt={ev.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-3 right-3 bg-stone-950/80 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full font-medium">
                      {lowestPrice === 0 ? "Free Entry" : `From ${fmtINR(lowestPrice)}`}
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="eyebrow text-xs mb-1.5">{ev.category}</div>
                      <h3 className="font-display font-bold text-lg group-hover:text-[color:var(--brand-terracotta)] transition-colors leading-snug">
                        {ev.title}
                      </h3>
                      {ev.subtitle && <p className="text-xs text-stone-500 mt-1 line-clamp-2">{ev.subtitle}</p>}
                    </div>

                    <div className="mt-5 pt-4 border-t border-border flex items-center justify-between text-xs text-stone-500">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={13} /> {fmtDateShort(ev.start_at)}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin size={13} /> {ev.venue_name || ev.city}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
